
import { Component, computed, inject, input, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { SelectDialogComponent } from '@scandium-oy/ngx-scandium';
import { format, isBefore, startOfDay, startOfToday } from 'date-fns';
import { take } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { SelectDayComponent } from 'src/app/components/select-day/select-day.component';
import { SelectTicketsDialog } from 'src/app/components/select-tickets/dialog/select-tickets.dialog';
import { AreaPickerComponent } from 'src/app/components2/area-picker/area-picker.component';
import { Contractor } from 'src/app/models/contractor.model';
import { Room } from 'src/app/models/room.model';
import { SiteArea } from 'src/app/models/site-area.model';
import { SiteProjectPeriod } from 'src/app/models/site-project-period.model';
import { SiteProjectPeriodTemplate } from 'src/app/models/site-project-template.model';
import { SiteProject } from 'src/app/models/site-project.model';
import { Site } from 'src/app/models/site.model';
import { SubItem } from 'src/app/models/sub-item.model';
import { RoomTicket, TicketStatus } from 'src/app/models/ticket.model';
import { AreasService } from 'src/app/services/areas.service';
import { ContractorsService } from 'src/app/services/contractors.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { PlusService } from 'src/app/services/plus.service';
import { ProjectsService } from 'src/app/services/projects.service';
import { SiteProjectPeriodsService } from 'src/app/services/site-project-period.service';
import { UsersService } from 'src/app/services/users.service';
import { WorkerTicketsService } from 'src/app/services/worker-tickets.service';
import { ModalShellComponent } from 'src/app/ui/modal-shell/modal-shell.component';
import { setAreaParents } from 'src/app/utility/kanban';
import { deepClone } from 'src/app/utility/object';
import { Roles } from 'src/app/utility/role';
import { defaultRoom } from 'src/app/utility/room';
import { convertTimestamp, getDateWithTimezone } from 'src/app/utility/time';

type ProjectJobGroupView = 'type' | 'area';

interface ProjectJobTicketView {
  ticket: RoomTicket;
  name: string;
  areaPath: string;
  assignee: string;
  hoursLabel: string;
  deadlineLabel: string;
  statusKey: TicketStatus;
  statusLabel: string;
  statusClass: string;
}

interface ProjectJobSummaryMetric {
  labelKey: string;
  value: number;
  tone?: 'default' | 'info' | 'success';
}

interface ProjectJobTicketGroup {
  key: string;
  title: string;
  subtitle: string;
  tickets: ProjectJobTicketView[];
  total: number;
  statuses: { key: TicketStatus; label: string; count: number; className: string }[];
}

@Component({
  selector: 'app-project-job-dialog',
  templateUrl: './job.dialog.html',
  styleUrls: ['./job.dialog.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AppCommonModule,
    AreaPickerComponent,
    SelectDayComponent,
    ModalShellComponent,
  ],
})
export class ProjectJobDialogComponent implements OnInit {
  private areasService = inject(AreasService);
  private contractorsService = inject(ContractorsService);
  private projectsService = inject(ProjectsService);
  private siteProjectPeriodsService = inject(SiteProjectPeriodsService);
  private usersService = inject(UsersService);
  private workerTicketsService = inject(WorkerTicketsService);
  private translate = inject(TranslateService);

  template = input<SiteProjectPeriodTemplate>();
  site = input.required<Site>();
  contractors = input<Contractor[]>();

  item = input<SiteProjectPeriod>();
  project = input<SiteProject>();
  contractor = input<string>();
  selectedContractor = signal<Contractor>(null);
  hasSelectContractor = computed(() => [Roles.manager, Roles.superAdmin].includes(this.usersService.role()) && this.item() == null);
  private actualTicketsByGuid = signal<Map<string, RoomTicket>>(new Map());

  title = signal<string>(null);
  activeGroupView = signal<ProjectJobGroupView>('type');
  areaRoots = signal<SiteArea[]>([]);
  areaPickerRoom = signal<Room>(null);
  formGroup: FormGroup;

  minDate: string;
  maxDate: string;

  constructor(
    private formBuilder: FormBuilder,
    private _modal: ModalController,
    private modalCtrl: ModalController,
    private navigationService: NavigationService,
    private plusService: PlusService,
  ) { }

  private getDate(time: Date | Timestamp) {
    return convertTimestamp(time).toISOString();
  }

  private getRooms() {
    const areas = this.formGroup.get('areas').value as {
      guid: string;
      name: string;
      id: string;
    }[];
    const templateTickets = this.template()?.tickets.map((t) => t.name) ?? [];
    this.workerTicketsService.createTicketsFromTemplate(areas, templateTickets, this.selectedContractor().guid, this.site()).then((tickets) => {
      const allTickets = [...this.formGroup.get('tickets').value, ...tickets];
      this.formGroup.get('tickets').setValue(allTickets);
    });
  }

  ngOnInit() {
    if (this.item()) {
      this.selectedContractor.set(this.item().contractor as Contractor);
    } else if (this.contractorsService.contractorS() != null) {
      this.selectedContractor.set(this.contractorsService.contractorS());
    } else {
      this.selectedContractor.set({ guid: this.contractor(), name: '' } as Contractor);
    }
    this.title.set(this.item()?.name ?? null);

    const projectStart = convertTimestamp(this.project().start);
    const projectEnd = convertTimestamp(this.project().end);
    this.minDate = getDateWithTimezone(projectStart);
    this.maxDate = getDateWithTimezone(projectEnd);

    const start = isBefore(startOfToday(), projectStart) ? projectStart.toISOString() : startOfToday().toISOString();

    this.formGroup = this.formBuilder.group({
      guid: [this.item()?.guid ?? null],
      name: [this.item()?.name ?? (this.template()?.name ?? ''), Validators.required],
      start: [
        this.item()?.start ? this.getDate(this.item().start) : start,
        Validators.required,
      ],
      end: [this.item()?.end ? this.getDate(this.item().end) : start, Validators.required],
      tickets: [this.item()?.tickets ?? []],
      areas: [this.item()?.areas ?? (this.project()?.areas ?? [])],
      project: [this.item()?.project ?? this.project().guid],
      doneIn: [this.item()?.doneIn ?? this.project().guid],
    });

    this.areasService.getSiteAreas(this.site()).pipe(
      take(1),
    ).subscribe((areas) => {
      const roots = (areas ?? []).map((area) => {
        const clone = deepClone(area);
        setAreaParents(clone);
        return clone;
      });
      this.areaRoots.set(roots);
    });

    if (this.project() != null) {
      this.workerTicketsService.getProjectTickets(this.project()).pipe(take(1)).subscribe((tickets) => {
        this.actualTicketsByGuid.set(new Map(tickets.map((ticket) => [ticket.guid, ticket])));
      });
    }
  }

  private resolveTicket(ticket: RoomTicket) {
    if (ticket?.guid && this.actualTicketsByGuid().has(ticket.guid)) {
      return this.actualTicketsByGuid().get(ticket.guid);
    }
    return ticket;
  }

  summaryMetrics(): ProjectJobSummaryMetric[] {
    const tickets = this.ticketViews();
    return [
      { labelKey: 'siteProject.jobDialog.summary.selectedAreas', value: this.selectedAreas().length },
      { labelKey: 'siteProject.jobDialog.summary.totalTasks', value: tickets.length },
      { labelKey: 'siteProject.jobDialog.summary.open', value: tickets.filter((ticket) => ticket.statusKey === TicketStatus.created).length },
      { labelKey: 'siteProject.jobDialog.summary.inProgress', value: tickets.filter((ticket) => ticket.statusKey === TicketStatus.inProgress).length, tone: 'info' },
      { labelKey: 'siteProject.jobDialog.summary.done', value: tickets.filter((ticket) => ticket.statusKey === TicketStatus.done).length, tone: 'success' },
    ];
  }

  groupedTickets() {
    return this.activeGroupView() === 'area' ? this.ticketGroupsByArea() : this.ticketGroupsByType();
  }

  setGroupView(view: ProjectJobGroupView) {
    this.activeGroupView.set(view);
  }

  isGroupView(view: ProjectJobGroupView) {
    return this.activeGroupView() === view;
  }

  private selectedAreas(): { name?: string; id?: string; guid?: string }[] {
    return (this.formGroup?.get('areas')?.value ?? []) as { name?: string; id?: string; guid?: string }[];
  }

  private ticketViews(): ProjectJobTicketView[] {
    const tickets = this.formGroup?.get('tickets')?.value ?? [];
    return tickets.map((ticket: RoomTicket) => ({
      ticket,
      name: this.getTicketName(ticket),
      areaPath: this.getTicketAreaPath(ticket),
      assignee: this.getTicketAssignee(ticket),
      hoursLabel: this.getTicketHoursLabel(ticket),
      deadlineLabel: this.getTicketDeadlineLabel(ticket),
      statusKey: this.getDisplayStatus(ticket),
      statusLabel: this.getTicketStatusLabel(ticket),
      statusClass: this.getTicketStatusClass(ticket),
    }));
  }

  private ticketGroupsByType(): ProjectJobTicketGroup[] {
    const grouped = new Map<string, ProjectJobTicketView[]>();
    for (const ticket of this.ticketViews()) {
      const key = ticket.name || this.translate.instant('siteProject.jobDialog.group.untitledTask');
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key).push(ticket);
    }

    return Array.from(grouped.entries())
      .map(([title, tickets]) => {
        const uniqueAreas = new Set(tickets.map((ticket) => ticket.areaPath).filter((area) => area !== this.translate.instant('siteProject.jobDialog.area.none')));
        const subtitle = uniqueAreas.size > 0
          ? this.translate.instant('siteProject.jobDialog.group.typeWithAreas', { tasks: tickets.length, areas: uniqueAreas.size })
          : this.translate.instant('siteProject.jobDialog.group.typeOnlyTasks', { tasks: tickets.length });
        return this.createTicketGroup(title, title, subtitle, tickets);
      })
      .sort((a, b) => a.title.localeCompare(b.title, 'fi'));
  }

  private ticketGroupsByArea(): ProjectJobTicketGroup[] {
    const grouped = new Map<string, ProjectJobTicketView[]>();

    for (const area of this.selectedAreas()) {
      const key = this.getAreaSelectionLabel(area);
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
    }

    for (const ticket of this.ticketViews()) {
      const key = ticket.areaPath;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key).push(ticket);
    }

    return Array.from(grouped.entries())
      .map(([title, tickets]) => {
        const uniqueTasks = new Set(tickets.map((ticket) => ticket.name));
        const subtitle = tickets.length > 0
          ? this.translate.instant('siteProject.jobDialog.group.areaWithTypes', { tasks: tickets.length, types: uniqueTasks.size })
          : this.translate.instant('siteProject.jobDialog.group.areaEmpty');
        return this.createTicketGroup(title, title, subtitle, tickets);
      })
      .sort((a, b) => a.title.localeCompare(b.title, 'fi'));
  }

  private createTicketGroup(key: string, title: string, subtitle: string, tickets: ProjectJobTicketView[]): ProjectJobTicketGroup {
    const statuses: TicketStatus[] = [TicketStatus.created, TicketStatus.inProgress, TicketStatus.done, TicketStatus.blocked, TicketStatus.late];
    return {
      key,
      title,
      subtitle,
      tickets: [...tickets].sort((a, b) => a.name.localeCompare(b.name, 'fi')),
      total: tickets.length,
      statuses: statuses
        .map((status) => ({
          key: status,
          label: `kanban.status.${status}`,
          count: tickets.filter((ticket) => ticket.statusKey === status).length,
          className: this.getTicketStatusClassByStatus(status),
        }))
        .filter((status) => status.count > 0),
    };
  }

  getAreaSelectionSummary() {
    const areas = this.selectedAreas();
    if (areas.length === 0) {
      return this.translate.instant('siteProject.jobDialog.areaSelection.none');
    }
    if (areas.length === 1) {
      return this.getAreaSelectionLabel(areas[0]);
    }
    return this.translate.instant('siteProject.jobDialog.areaSelection.many', { count: areas.length });
  }

  getAreaSelectionItems() {
    return this.selectedAreas().map((area) => this.getAreaSelectionLabel(area));
  }

  getAreaSelectionEntries() {
    return this.selectedAreas().map((area) => ({
      guid: area.guid ?? null,
      id: area.id ?? null,
      label: this.getAreaSelectionLabel(area),
    }));
  }

  private getAreaSelectionLabel(area: { name?: string; id?: string }) {
    if (area == null) {
      return this.translate.instant('siteProject.jobDialog.area.unknown');
    }
    if (area.name && area.id && area.id !== area.name) {
      return `${String(area.name)} / ${String(area.id)}`;
    }
    return area.name ?? area.id ?? this.translate.instant('siteProject.jobDialog.area.unknown');
  }

  onAreaPickerSelected(room: Room) {
    if (!room?.siteArea) {
      return;
    }
    const current = this.formGroup.get('areas').value ?? [];
    const nextItem = {
      guid: room.siteArea.root,
      name: room.siteArea.name,
      id: room.siteArea.id,
    };
    const exists = current.some((item: { guid?: string; id?: string }) => item.guid === nextItem.guid && item.id === nextItem.id);
    if (exists) {
      return;
    }
    this.formGroup.get('areas').setValue([...current, nextItem]);
    this.areaPickerRoom.set(room);
    this.getRooms();
  }

  removeAreaSelection(area: { guid?: string; id?: string }) {
    const current = this.formGroup.get('areas').value ?? [];
    const next = current.filter((item: { guid?: string; id?: string }) => !(item.guid === area.guid && item.id === area.id));
    this.formGroup.get('areas').setValue(next);
  }

  selectContractor() {
    const items = this.contractors() ?? [];
    const clearButton = this.selectedContractor() != null;
    this.modalCtrl.create({ component: SelectDialogComponent<Contractor>, componentProps: { items, clearButton }, cssClass: ['stack-modal'] }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data) {
          if (data.data.clear) {
            this.selectedContractor.set(null);
          } else {
            if (data.data.guid === null) {
              const site = this.site();
              this.projectsService.inviteNewContractor(site).then((contractor) => {
                this.selectedContractor.set(contractor);
              });
            } else {
              this.selectedContractor.set(data.data);
            }
          }
        }
      });
    });
  }

  onStartDateChange(value: string) {
    const normalizedValue = this.toDateTimeValue(value);
    this.formGroup.get('start').setValue(normalizedValue);
    const end = this.formGroup.get('end').value;
    if (end && new Date(normalizedValue) > new Date(end)) {
      this.formGroup.get('end').setValue(normalizedValue);
    }
  }

  onEndDateChange(value: string) {
    const normalizedValue = this.toDateTimeValue(value);
    this.formGroup.get('end').setValue(normalizedValue);
    const start = this.formGroup.get('start').value;
    if (start && new Date(normalizedValue) < new Date(start)) {
      this.formGroup.get('start').setValue(normalizedValue);
    }
  }

  toDateInput(value: string | Date | Timestamp) {
    if (value == null) {
      return '';
    }
    return format(convertTimestamp(value), 'yyyy-MM-dd');
  }

  private toDateTimeValue(value: string) {
    return startOfDay(new Date(value)).toISOString();
  }

  addNewTicket() {
    const item = this.formGroup.getRawValue();
    item.contractor = this.selectedContractor();
    this.modalCtrl.create({ component: SelectTicketsDialog, componentProps: { site: this.site(), hasAdd: true, onlyOne: true }, cssClass: ['stack-modal'] }).then((m) => {
      m.present();

      m.onDidDismiss<{ tickets?: RoomTicket[]; new: boolean }>().then((data) => {
        if (data.data) {
          if (data.data.new) {
            this.plusService.onNewTask([this.site()], this.project(), item, false, true).then((tickets) => {
              if (tickets?.length > 0) {
                const allTickets = [...this.formGroup.get('tickets').value, ...tickets];
                this.formGroup.get('tickets').setValue(allTickets);
                if (item.guid) {
                  tickets.map(async (t) => this.siteProjectPeriodsService.addTickets(item.guid, t));
                }
              }
            });
          } else if (data.data.tickets?.length > 0) {
            const items = data.data.tickets.map((roomTicket) => {
              const rt = {
                guid: roomTicket.guid,
                name: roomTicket.name,
                parent: roomTicket.parent?.guid ?? null,
              };
              this.siteProjectPeriodsService.addTickets(item.guid, rt);
              return rt;
            });
            const allTickets = [...this.formGroup.get('tickets').value, ...items];
            this.formGroup.get('tickets').setValue(allTickets);
          }
        }
      });
    });
  }

  openTicket(item: SubItem | RoomTicket) {
    const parentGuid = 'parentGuid' in item ? item.parentGuid : null;
    const parent = typeof item.parent === 'string' ? item.parent : item.parent?.guid ?? parentGuid ?? null;
    this.workerTicketsService.openRoomTicket(parent, item.guid, false, ['workspace-modal', 'stack-modal']);
  }

  private getTicketName(ticket: RoomTicket) {
    const resolved = this.resolveTicket(ticket);
    return resolved?.plainName ?? resolved?.name ?? ticket?.name ?? this.translate.instant('siteProject.jobDialog.group.untitledTask');
  }

  getTicketAreaPath(ticket: RoomTicket) {
    const resolved = this.resolveTicket(ticket);
    const parentAreaName = typeof resolved?.parent === 'object' && resolved.parent != null ? resolved.parent.areaName : null;
    const roomAreaName = resolved?.room?.siteArea?.name ?? null;
    const roomId = resolved?.room?.id ?? null;

    const parts = [roomAreaName || parentAreaName, roomId]
      .filter((part) => part != null && part !== '' && part !== defaultRoom);

    if (parts.length > 0) {
      return parts.join(' / ');
    }

    if (resolved?.area) {
      return resolved.area;
    }

    return this.translate.instant('siteProject.jobDialog.area.none');
  }

  getTicketAssignee(ticket: RoomTicket) {
    const resolved = this.resolveTicket(ticket);
    const assignedGuid = resolved?.user ?? resolved?.users?.[0] ?? null;
    const assignedUser = assignedGuid ? this.usersService.getSync(assignedGuid) : null;
    return resolved?.username
      ?? resolved?.usernames?.[0]
      ?? assignedUser?.displayName
      ?? this.translate.instant('siteProject.jobDialog.ticket.noAssignee');
  }

  getTicketHoursLabel(ticket: RoomTicket) {
    const resolved = this.resolveTicket(ticket);
    const hours = Number(resolved?.hours ?? 0);
    return hours > 0 ? `${hours}h` : this.translate.instant('siteProject.jobDialog.ticket.noReservation');
  }

  getTicketDeadlineLabel(ticket: RoomTicket) {
    const resolved = this.resolveTicket(ticket);
    if (resolved?.deadline == null) {
      return this.translate.instant('siteProject.jobDialog.ticket.noDeadline');
    }
    const deadline = convertTimestamp(resolved.deadline);
    return Number.isNaN(deadline.getTime()) ? this.translate.instant('siteProject.jobDialog.ticket.noDeadline') : format(deadline, 'd.M.yyyy');
  }

  private getDisplayStatus(ticket: RoomTicket) {
    const resolved = this.resolveTicket(ticket);
    switch (resolved?.status) {
      case TicketStatus.done:
      case TicketStatus.checked:
        return TicketStatus.done;
      case TicketStatus.inProgress:
        return TicketStatus.inProgress;
      case TicketStatus.blocked:
      case TicketStatus.paused:
        return TicketStatus.blocked;
      case TicketStatus.late:
        return TicketStatus.late;
      case TicketStatus.created:
      case TicketStatus.deleted:
      case TicketStatus.declined:
      default:
        return TicketStatus.created;
    }
  }

  getTicketStatusLabel(ticket: RoomTicket) {
    return `kanban.status.${this.getDisplayStatus(ticket)}`;
  }

  getTicketStatusClass(ticket: RoomTicket) {
    return this.getTicketStatusClassByStatus(this.getDisplayStatus(ticket));
  }

  private getTicketStatusClassByStatus(status: TicketStatus) {
    switch (status) {
      case TicketStatus.done:
        return 'project-job-dialog-status-chip project-job-dialog-status-chip-success';
      case TicketStatus.inProgress:
        return 'project-job-dialog-status-chip project-job-dialog-status-chip-info';
      case TicketStatus.blocked:
      case TicketStatus.paused:
        return 'project-job-dialog-status-chip project-job-dialog-status-chip-warning';
      case TicketStatus.late:
        return 'project-job-dialog-status-chip project-job-dialog-status-chip-danger';
      case TicketStatus.checked:
      case TicketStatus.created:
      case TicketStatus.deleted:
      case TicketStatus.declined:
      default:
        return 'project-job-dialog-status-chip project-job-dialog-status-chip-neutral';
    }
  }

  ticketTracking() {
    const site = this.site();
    this.navigationService.navigateToSiteTickets(site.guid, this.project().guid, this.title());
    this.dismiss();
  }

  save() {
    const item = this.formGroup.value;
    item.start = startOfDay(new Date(item.start));
    item.end = startOfDay(new Date(item.end));
    item.contractor = { guid: this.selectedContractor().guid, name: this.selectedContractor().name };
    this._modal.dismiss(item);
  }

  dismiss() {
    this._modal.dismiss();
  }
}
